import { watch } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw, RouteMeta } from 'vue-router'
import i18n from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { authErrorWord } from '@/utils/authFlow'
import { useWorkspaceStore } from '@/stores/workspace'
import { useUIStore } from '@/stores/ui'
import type { FeatureFlag } from '@/types'
import { canonicalContentWorkflowLocation, contentWorkflowEditorPath } from '@/composables/useContentWorkflowStage'

// Route meta types
declare module 'vue-router' {
  interface RouteMeta {
    layout?: 'app' | 'auth' | 'public' | 'none'
    requiresAuth?: boolean
    requiredFeature?: FeatureFlag
    title?: string
    // Translation key for `title` — what the browser tab shows in the user's language.
    titleKey?: string
    // The main sidebar swaps its content for this route family ('settings'
    // renders the settings menu in place of the main nav; 'knowledge' and
    // 'page-settings' do the same with their own menus).
    navVariant?: 'settings' | 'profile' | 'knowledge' | 'page-settings' | 'blog-settings' | 'email-settings' | 'social-settings'
  }
}

// App routes (require workspaceId)
const appRoutes: RouteRecordRaw[] = [
  // Redirect /:workspaceId to /:workspaceId/home
  {
    path: '/:workspaceId',
    redirect: to => `/${to.params.workspaceId}/home`
  },
  // Dashboard
  {
    path: '/:workspaceId/home',
    name: 'dashboard',
    component: () => import('@/pages/app/DashboardPageUS.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Dashboard', titleKey: 'routeTitle.dashboard' }
  },

  // Pages
  {
    path: '/:workspaceId/pages',
    name: 'pages',
    component: () => import('@/pages/app/pages/PagesListPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'pages' as FeatureFlag, title: 'Pages', titleKey: 'nav.pages' }
  },
  {
    path: '/:workspaceId/pages/create',
    name: 'pages-create',
    redirect: to => ({
      name: 'pages',
      params: { workspaceId: to.params.workspaceId },
      query: { create: 'true' },
    }),
  },
  {
    path: '/:workspaceId/pages/create-website',
    name: 'pages-create-website',
    component: () => import('@/pages/app/pages/CreateWebsitePage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'pages' as FeatureFlag, title: 'Create Website', titleKey: 'routeTitle.createWebsite' }
  },
  {
    path: '/:workspaceId/pages/archived',
    name: 'pages-archived',
    component: () => import('@/pages/app/pages/ArchivedPagesPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'pages' as FeatureFlag, title: 'Archived Pages', titleKey: 'routeTitle.archivedPages' }
  },
  {
    path: '/:workspaceId/:pageId/editor',
    name: 'page-editor',
    component: () => import('@/pages/app/pages/PageEditorPage.vue'),
    meta: { layout: 'none', requiresAuth: true, requiredFeature: 'pages' as FeatureFlag, title: 'Design Page', titleKey: 'routeTitle.designPage' }
  },
  // Per-page settings — its own full screen (was a small popup inside the
  // builder). Uses the normal app shell, with the sidebar swapped for the
  // page-settings menu (navVariant below), exactly like /settings and
  // /knowledge do. Ranking note: every competing 3-segment route has a STATIC
  // second segment (/:workspaceId/settings/:tab?, /:workspaceId/knowledge/:tab,
  // …), which vue-router scores above a param, so /16/settings/branding still
  // resolves to Team Settings and only /16/<pageId>/settings lands here.
  {
    path: '/:workspaceId/:pageId/settings/:tab?',
    name: 'page-settings',
    component: () => import('@/pages/app/pages/PageSettingsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'pages' as FeatureFlag, title: 'Page Settings', titleKey: 'routeTitle.pageSettings', navVariant: 'page-settings' }
  },
  {
    path: '/:workspaceId/pages/preview/:slug(.*)',
    name: 'page-preview',
    component: () => import('@/pages/app/pages/PagePreviewPage.vue'),
    meta: { layout: 'none', requiresAuth: true, requiredFeature: 'pages' as FeatureFlag, title: 'Preview', titleKey: 'routeTitle.preview' }
  },

  // Website form submission history
  {
    path: '/:workspaceId/forms',
    name: 'forms',
    component: () => import('@/pages/app/forms/FormsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'pages' as FeatureFlag, title: 'Forms', titleKey: 'nav.forms' }
  },
  {
    path: '/:workspaceId/forms/submissions/:submissionId',
    name: 'form-submission',
    component: () => import('@/pages/app/forms/FormSubmissionPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'pages' as FeatureFlag, title: 'Form submission', titleKey: 'forms.submission' }
  },

  // Collections
  {
    path: '/:workspaceId/collections',
    name: 'collections',
    component: () => import('@/pages/app/collections/CollectionsListPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'collections' as FeatureFlag, title: 'Collections', titleKey: 'nav.collections' }
  },
  {
    path: '/:workspaceId/collections/:collectionId',
    name: 'collection-detail',
    component: () => import('@/pages/app/collections/CollectionDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'collections' as FeatureFlag, title: 'Collection', titleKey: 'routeTitle.collection' },
    children: [
      {
        path: 'records/:recordId',
        name: 'collection-record',
        component: () => import('@/pages/app/collections/CollectionRecordPage.vue'),
        meta: { title: 'Collection record', titleKey: 'routeTitle.collectionRecord' }
      }
    ]
  },

  // Catalog
  {
    path: '/:workspaceId/catalog',
    name: 'catalog',
    component: () => import('@/pages/app/catalog/CatalogListPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'catalog' as FeatureFlag, title: 'Catalog', titleKey: 'nav.catalog' }
  },
  {
    path: '/:workspaceId/catalog/create',
    name: 'catalog-create',
    component: () => import('@/pages/app/catalog/CreateProductPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'catalog' as FeatureFlag, title: 'Create Product', titleKey: 'routeTitle.createProduct' }
  },
  // Orders — the catalog storefront order book, promoted to the top-level
  // `/orders` URL (was `/catalog/orders`). The legacy single-page `/orders`
  // system was removed 2026-07-02; old paths redirect below for stale links.
  {
    path: '/:workspaceId/orders',
    name: 'catalog-orders',
    component: () => import('@/pages/app/catalog/CatalogOrdersListPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'catalog' as FeatureFlag, title: 'Orders', titleKey: 'nav.orders' }
  },
  { path: '/:workspaceId/catalog/orders', redirect: (to) => `/${to.params.workspaceId}/orders` },
  { path: '/:workspaceId/catalog/discounts', redirect: (to) => `/${to.params.workspaceId}/discounts` },
  {
    path: '/:workspaceId/discounts',
    name: 'catalog-discounts',
    component: () => import('@/pages/app/catalog/CatalogDiscountsListPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'catalog' as FeatureFlag, title: 'Discounts', titleKey: 'nav.discounts' }
  },
  // Create is a popup on the list page now — old create-page URL opens it.
  // Must precede :discountId so "create" isn't read as a discount id.
  { path: '/:workspaceId/discounts/create', redirect: (to) => ({ path: `/${to.params.workspaceId}/discounts`, query: { create: 'true' } }) },
  {
    path: '/:workspaceId/discounts/:discountId',
    name: 'catalog-discount-edit',
    component: () => import('@/pages/app/catalog/CatalogDiscountFormPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'catalog' as FeatureFlag, title: 'Edit Discount', titleKey: 'routeTitle.editDiscount' }
  },
  {
    path: '/:workspaceId/orders/:orderId',
    name: 'catalog-order-detail',
    component: () => import('@/pages/app/catalog/CatalogOrderDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'catalog' as FeatureFlag, title: 'Order', titleKey: 'routeTitle.order' }
  },
  { path: '/:workspaceId/catalog/orders/:orderId', redirect: (to) => `/${to.params.workspaceId}/orders/${to.params.orderId}` },
  {
    path: '/:workspaceId/catalog/:productId',
    name: 'catalog-detail',
    component: () => import('@/pages/app/catalog/ProductDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'catalog' as FeatureFlag, title: 'Product', titleKey: 'routeTitle.product' }
  },
  { path: '/:workspaceId/shipping-rates', redirect: (to) => `/${to.params.workspaceId}/shipping` },
  {
    path: '/:workspaceId/shipping',
    name: 'shipping-rates',
    component: () => import('@/pages/app/catalog/ShippingRatesPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'catalog' as FeatureFlag, title: 'Shipping Rates', titleKey: 'routeTitle.shippingRates' }
  },
  // Dev-only preview for catalog system pages — embeds the backend's
  // /dev/preview route in an iframe so the cart in the app's localStorage
  // can be passed through. The route still mounts in prod builds; the
  // backend dev route is what's actually gated by NODE_ENV.
  {
    path: '/:workspaceId/catalog/:slug(checkout-preview|thank-you-preview)',
    name: 'catalog-system-preview',
    component: () => import('@/pages/app/catalog/CatalogSystemPreviewPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'catalog' as FeatureFlag, title: 'Preview', titleKey: 'routeTitle.preview' }
  },

  // Content — unified list of Emails + Social + Blogs (replaces the three
  // separate channel pages). The bare channel routes (/:wsid/social etc.)
  // redirect here for any old bookmarks / external links.
  {
    path: '/:workspaceId/content',
    name: 'content',
    component: () => import('@/pages/app/content/ContentListPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Content', titleKey: 'nav.content' }
  },

  // Business Brain — client view of the contextEngine. One page per section;
  // the sidebar swaps to its menu (navVariant 'knowledge') to navigate
  // between them. The :tab segment carries the section slug; the bare path
  // lands on Overview (Jens 2026-08-17, which also renamed the URL from
  // /knowledge to /brain — the old paths still redirect, below).
  {
    path: '/:workspaceId/brain',
    redirect: to => `/${to.params.workspaceId}/brain/overview`
  },
  { path: '/:workspaceId/knowledge', redirect: to => `/${to.params.workspaceId}/brain/overview` },
  {
    path: '/:workspaceId/knowledge/:tab',
    redirect: to => `/${to.params.workspaceId}/brain/${to.params.tab}`,
  },

  // Media Library — standalone view over the page-builder's media library
  // (workspace image + video assets). Planned home for photo/video editing.
  {
    path: '/:workspaceId/media',
    name: 'media-library',
    component: () => import('@/pages/app/media-library/MediaLibraryPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Media', titleKey: 'nav.mediaLibrary' }
  },
  // Media Editor — full-screen image/video editor (layout 'none').
  // The same route handles a brand-new design via documentId='new': the page
  // component creates the doc, then router.replace()s to the real id URL. No
  // separate /new route is needed. Optional ?kind=image|video&preset=<key>
  // seed the create.
  {
    path: '/:workspaceId/media/:documentId/edit',
    name: 'media-editor',
    component: () => import('@/pages/app/media-editor/MediaEditorPage.vue'),
    meta: { layout: 'none', requiresAuth: true, title: 'Media Editor', titleKey: 'routeTitle.mediaEditor' }
  },
  {
    path: '/:workspaceId/brain/:tab',
    name: 'knowledge',
    component: () => import('@/pages/app/knowledge/KnowledgePage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Business Brain', titleKey: 'nav.knowledge', navVariant: 'knowledge' }
  },

  // Content Creation - Social (path-param: `new` or numeric id)
  // Bare /:workspaceId/social forwards to the unified Content page on the
  // Social tab — preserves old bookmarks and the BR landing-page sidebar's
  // deep-link semantics.
  {
    path: '/:workspaceId/social',
    redirect: to => ({ path: `/${to.params.workspaceId}/content`, query: { channel: 'social' } })
  },
  {
    path: '/:workspaceId/social/:contentId/settings/:tab',
    name: 'social-settings',
    redirect: to => ({ name: 'social', params: { workspaceId: to.params.workspaceId, contentId: to.params.contentId, stage: '' }, query: { ...to.query, stage: undefined, panel: 'info' }, hash: to.hash }),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'social' as FeatureFlag, title: 'Social Post Settings', titleKey: 'routeTitle.socialPostSettings', navVariant: 'social-settings' }
  },
  {
    path: contentWorkflowEditorPath('social'),
    name: 'social',
    component: () => import('@/pages/app/content/CreateSocialPostPage.vue'),
    meta: { layout: 'none', requiresAuth: true, requiredFeature: 'social' as FeatureFlag, title: 'Social Post', titleKey: 'routeTitle.socialPost' }
  },

  // Content Creation - Blog
  {
    path: '/:workspaceId/blog',
    redirect: to => ({ path: `/${to.params.workspaceId}/content`, query: { channel: 'blog' } })
  },
  {
    path: '/:workspaceId/blog/:contentId/settings/:tab',
    name: 'blog-settings',
    beforeEnter: to => to.params.tab === 'code' ? true : ({ name: 'blog', params: { workspaceId: to.params.workspaceId, contentId: to.params.contentId, stage: '' }, query: { ...to.query, stage: undefined, panel: 'info' }, hash: to.hash }),
    component: () => import('@/pages/app/content/BlogSettingsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'blog' as FeatureFlag, title: 'Blog Post Settings', titleKey: 'routeTitle.blogPostSettings', navVariant: 'blog-settings' }
  },
  {
    path: contentWorkflowEditorPath('blog'),
    name: 'blog',
    component: () => import('@/pages/app/content/CreateBlogPostPage.vue'),
    meta: { layout: 'none', requiresAuth: true, requiredFeature: 'blog' as FeatureFlag, title: 'Blog Post', titleKey: 'routeTitle.blogPost' }
  },

  // Lead Magnets — federated list (LeadMagnet PDFs + email-sequence Campaigns
  // flagged as lead magnets). Lives at /magnets; old /lead-magnets redirects.
  { path: '/:workspaceId/lead-magnets', redirect: (to) => `/${to.params.workspaceId}/magnets` },
  { path: '/:workspaceId/lead-magnets/pdf/:id', redirect: (to) => `/${to.params.workspaceId}/magnets/pdf/${to.params.id}` },
  {
    path: '/:workspaceId/magnets',
    name: 'lead-magnets',
    component: () => import('@/pages/app/content/LeadMagnetsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'blog' as FeatureFlag, title: 'Magnets', titleKey: 'nav.leadMagnets' }
  },
  {
    path: '/:workspaceId/magnets/interactive/:id',
    name: 'lead-magnet-interactive',
    component: () => import('@/pages/app/content/InteractiveMagnetPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'blog' as FeatureFlag, title: 'Magnet', titleKey: 'routeTitle.magnet' }
  },
  {
    path: '/:workspaceId/magnets/pdf/:id',
    name: 'lead-magnet-pdf',
    component: () => import('@/pages/app/content/LeadMagnetPdfDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'blog' as FeatureFlag, title: 'Magnet', titleKey: 'routeTitle.magnet' }
  },

  // Content Creation - Email
  {
    path: '/:workspaceId/email/:contentId/report',
    name: 'email-results',
    redirect: to => ({ name: 'email', params: { workspaceId: to.params.workspaceId, contentId: to.params.contentId, stage: 'results' }, query: { ...to.query, stage: undefined }, hash: to.hash }),
  },
  {
    path: '/:workspaceId/email',
    redirect: to => ({ path: `/${to.params.workspaceId}/content`, query: { channel: 'email' } })
  },
  {
    path: '/:workspaceId/email/:contentId/settings/:tab',
    name: 'email-settings',
    redirect: to => ({ name: 'email', params: { workspaceId: to.params.workspaceId, contentId: to.params.contentId, stage: '' }, query: { ...to.query, stage: undefined, panel: 'info' }, hash: to.hash }),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'email' as FeatureFlag, title: 'Email Settings', titleKey: 'routeTitle.emailSettings', navVariant: 'email-settings' }
  },
  {
    path: contentWorkflowEditorPath('email'),
    name: 'email',
    component: () => import('@/pages/app/content/CreateEmailPage.vue'),
    meta: { layout: 'none', requiresAuth: true, requiredFeature: 'email' as FeatureFlag, title: 'Email', titleKey: 'routeTitle.email' }
  },

  // Content Creation - Keyword Ads
  {
    path: '/:workspaceId/keyword',
    name: 'keyword',
    component: () => import('@/pages/app/content/CreateKeywordAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'keywordads' as FeatureFlag, title: 'Keyword Ads', titleKey: 'nav.keywordAds' }
  },

  // Campaigns — multi-channel rollups (theme + period → many items × many
  // content pieces). List + detail mirror the Sequences pattern; detail
  // links per content row into the channel editor at /:channel/:id.
  {
    path: '/:workspaceId/campaigns',
    name: 'campaigns',
    component: () => import('@/pages/app/campaigns/CampaignsListPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'campaigns' as FeatureFlag, title: 'Campaigns', titleKey: 'nav.campaigns' }
  },
  {
    path: '/:workspaceId/campaigns/:campaignId',
    name: 'campaign-detail',
    component: () => import('@/pages/app/campaigns/CampaignDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'campaigns' as FeatureFlag, title: 'Campaign', titleKey: 'routeTitle.campaign' }
  },

  // Keep legacy sequence and launch links working; current sequence creation
  // lives in the Sequences section.
  {
    path: '/:workspaceId/sequence',
    redirect: to => `/${to.params.workspaceId}/content`
  },

  // Viewer for existing standalone paid_ad Content rows.
  {
    path: '/:workspaceId/ads/:contentId',
    name: 'paid-ad-viewer',
    component: () => import('@/pages/app/content/PaidAdViewerPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Paid ad', titleKey: 'routeTitle.paidAd' }
  },
  {
    path: '/:workspaceId/launch',
    redirect: to => `/${to.params.workspaceId}/content`
  },

  // Calendar (replaces the legacy Scheduler / GeneratedContentPage)
  {
    path: '/:workspaceId/calendar',
    name: 'calendar',
    component: () => import('@/pages/app/calendar/CalendarPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'scheduler' as FeatureFlag, title: 'Calendar', titleKey: 'nav.calendar' }
  },

  // Studio → Keywords. The keyword bank: imports from Claude Cowork
  // or Semrush, status-managed (candidate/targeted/won/parked/dropped),
  // feeds the activation.keyword_universe context entity that the
  // blog generator reads at write-time.
  {
    path: '/:workspaceId/keywords',
    name: 'keywords',
    component: () => import('@/pages/app/keywords/KeywordsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'blog' as FeatureFlag, title: 'Keywords', titleKey: 'nav.keywords' }
  },
  // Ads — unified screen for keyword (Google) + platform (FB/IG/etc) ads.
  // Phase 1+2 of the Ads section (2026-05-17). Google-first; social tile
  // is "Coming soon" until Google is vetted.
  {
    path: '/:workspaceId/ads',
    name: 'ads',
    component: () => import('@/pages/app/ads/AdsListPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Ads', titleKey: 'nav.ads' }
  },
  {
    path: '/:workspaceId/ads/create/google',
    name: 'ads-create-google',
    component: () => import('@/pages/app/ads/CreateGoogleAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create Google Ad', titleKey: 'routeTitle.createGoogleAd' }
  },
  {
    path: '/:workspaceId/ads/create/meta',
    name: 'ads-create-meta',
    component: () => import('@/pages/app/ads/CreateMetaAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create Meta Ad', titleKey: 'routeTitle.createMetaAd' }
  },
  {
    path: '/:workspaceId/ads/create/microsoft',
    name: 'ads-create-microsoft',
    component: () => import('@/pages/app/ads/CreateMicrosoftAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create Microsoft Ad', titleKey: 'routeTitle.createMicrosoftAd' }
  },
  {
    path: '/:workspaceId/ads/create/reddit',
    name: 'ads-create-reddit',
    component: () => import('@/pages/app/ads/CreateRedditAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create Reddit Ad', titleKey: 'routeTitle.createRedditAd' }
  },
  {
    path: '/:workspaceId/ads/create/tiktok',
    name: 'ads-create-tiktok',
    component: () => import('@/pages/app/ads/CreateTikTokAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create TikTok Ad', titleKey: 'routeTitle.createTikTokAd' }
  },
  {
    path: '/:workspaceId/ads/create/youtube',
    name: 'ads-create-youtube',
    component: () => import('@/pages/app/ads/CreateYouTubeAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create YouTube Ad', titleKey: 'routeTitle.createYouTubeAd' }
  },
  {
    path: '/:workspaceId/ads/create/pinterest',
    name: 'ads-create-pinterest',
    component: () => import('@/pages/app/ads/CreatePinterestAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create Pinterest Ad', titleKey: 'routeTitle.createPinterestAd' }
  },
  {
    path: '/:workspaceId/ads/create/linkedin',
    name: 'ads-create-linkedin',
    component: () => import('@/pages/app/ads/CreateLinkedInAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create LinkedIn Ad', titleKey: 'routeTitle.createLinkedInAd' }
  },
  {
    path: '/:workspaceId/ads/create/amazon',
    name: 'ads-create-amazon',
    component: () => import('@/pages/app/ads/CreateAmazonAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create Amazon Ad', titleKey: 'routeTitle.createAmazonAd' }
  },
  {
    path: '/:workspaceId/ads/create/nextdoor',
    name: 'ads-create-nextdoor',
    component: () => import('@/pages/app/ads/CreateNextdoorAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create Nextdoor Ad', titleKey: 'routeTitle.createNextdoorAd' }
  },
  {
    path: '/:workspaceId/ads/create/yelp',
    name: 'ads-create-yelp',
    component: () => import('@/pages/app/ads/CreateYelpAdPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Create Yelp Ad', titleKey: 'routeTitle.createYelpAd' }
  },
  // Legacy redirect — bookmarks + external links to the old Scheduler URL
  // land on the new Calendar.
  {
    path: '/:workspaceId/generated-content',
    redirect: to => `/${to.params.workspaceId}/calendar`
  },

  // Sequences — event-triggered email flows (filter of email Campaigns
  // where emailShape='sequence'). Different from Calendar, which is dated.
  {
    path: '/:workspaceId/sequences',
    name: 'sequences',
    component: () => import('@/pages/app/sequences/SequencesListPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'email.sequences' as FeatureFlag, title: 'Sequences', titleKey: 'nav.sequences' }
  },
  {
    path: '/:workspaceId/sequences/:sequenceId',
    name: 'sequence-detail',
    component: () => import('@/pages/app/sequences/SequenceDetailPage.vue'),
    meta: { layout: 'none', requiresAuth: true, requiredFeature: 'email.sequences' as FeatureFlag, title: 'Sequence', titleKey: 'routeTitle.sequence' }
  },

  // Contacts is the complete directory. Old list links open the matching
  // saved view; record links below still open the same Contact record.
  {
    path: '/:workspaceId/contacts',
    name: 'contacts',
    component: () => import('@/pages/app/ContactsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'customers' as FeatureFlag, title: 'Contacts', titleKey: 'nav.contacts' }
  },
  {
    path: '/:workspaceId/contacts/:contactId',
    name: 'contact-detail',
    component: () => import('@/pages/app/ContactDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'customers' as FeatureFlag, title: 'Contact', titleKey: 'routeTitle.contact' }
  },
  // Each Prospecting screen has its own address. Older links keep their query.
  {
    path: '/:workspaceId/prospecting',
    redirect: to => ({ path: `/${to.params.workspaceId}/prospecting/prospects`, query: to.query, hash: to.hash })
  },
  {
    path: '/:workspaceId/prospecting/prepared',
    redirect: to => ({ path: `/${to.params.workspaceId}/prospecting/emails`, query: to.query, hash: to.hash })
  },
  {
    path: '/:workspaceId/prospecting/:tab(prospects|outreach|emails|mailboxes)',
    name: 'prospecting',
    component: () => import('@/pages/app/prospecting/ProspectingPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'prospecting' as FeatureFlag, title: 'Prospecting', titleKey: 'routeTitle.prospecting' }
  },
  // One outreach's setup is its own page: a new one, or an existing one by id.
  {
    path: '/:workspaceId/prospecting/outreach/:outreachId(new|\\d+)',
    name: 'prospecting-outreach',
    component: () => import('@/pages/app/prospecting/OutreachSetupPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'prospecting' as FeatureFlag, title: 'Outreach', titleKey: 'routeTitle.prospecting' }
  },
  // One sending mailbox's status is its own page, by its number (2026-11-02).
  {
    path: '/:workspaceId/prospecting/mailboxes/:mailboxId(\\d+)',
    name: 'prospecting-mailbox',
    component: () => import('@/pages/app/prospecting/MailboxPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'prospecting' as FeatureFlag, title: 'Mailbox', titleKey: 'routeTitle.prospecting' }
  },
  {
    path: '/:workspaceId/customers',
    name: 'customers',
    redirect: to => ({ path: `/${to.params.workspaceId}/contacts`, query: { ...to.query, view: 'customers', tab: undefined,
      status: ['all', 'active', 'archived'].includes(String(to.query.tab)) ? String(to.query.tab) : to.query.status } })
  },
  {
    path: '/:workspaceId/customers/:customerId',
    name: 'customer-detail',
    component: () => import('@/pages/app/CustomerDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'customers' as FeatureFlag, title: 'Contact', titleKey: 'routeTitle.contact' }
  },

  // Leads. Contacts, Leads and Deals share one feature switch: `customers`.
  {
    path: '/:workspaceId/leads',
    name: 'leads',
    redirect: to => {
      const query = { ...to.query }
      // A saved view bookmark is more specific than the old Leads landing tab.
      const legacyTabs: Record<string, string> = {
        all: 'leads-all', mine: 'leads-mine', unassigned: 'leads-unassigned',
        due_today: 'leads-due_today', overdue: 'leads-overdue',
        no_next_action: 'leads-no_next_action', ready: 'leads-ready',
      }
      if (typeof query.tab === 'string' && query.tab.startsWith('view:')) {
        // Keep migrated saved-view bookmarks intact.
      } else if (typeof query.tab === 'string' && legacyTabs[query.tab]) {
        query.legacyView = legacyTabs[query.tab]!
        delete query.tab
      } else if (!query.legacyView) query.legacyView = 'sales-follow-up'
      return { path: `/${to.params.workspaceId}/contacts`, query }
    }
  },
  // Saved batch links from search summaries still reach Research.
  {
    path: '/:workspaceId/leads/prospects',
    redirect: to => ({ path: `/${to.params.workspaceId}/prospecting/research`, query: to.query, hash: to.hash })
  },
  {
    path: '/:workspaceId/prospecting/research',
    name: 'prospects-review',
    component: () => import('@/pages/app/ProspectsReviewPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'prospecting' as FeatureFlag, title: 'Research', titleKey: 'routeTitle.prospectsToReview' }
  },
  {
    path: '/:workspaceId/leads/:leadId(\\d+)',
    name: 'lead-detail',
    component: () => import('@/pages/app/LeadDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'customers' as FeatureFlag, title: 'Contact', titleKey: 'routeTitle.contact' }
  },

  {
    path: '/:workspaceId/deals',
    name: 'deals',
    component: () => import('@/pages/app/DealsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'customers' as FeatureFlag, title: 'Deals', titleKey: 'nav.deals' }
  },
  {
    path: '/:workspaceId/deals/:dealId',
    name: 'deal-detail',
    component: () => import('@/pages/app/DealDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'customers' as FeatureFlag, title: 'Deal', titleKey: 'routeTitle.deal' }
  },

  // Inbox — one normal top-level page. Queue/status is an in-page filter,
  // defaulting to Needs you. Legacy submenu URLs redirect into that filter.
  // A connected channel always feeds the Inbox; website chat's own setup lives
  // under Settings > Website chat.
  {
    path: '/:workspaceId/inbox',
    name: 'inbox',
    component: () => import('@/pages/app/inbox/InboxPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'inbox' as FeatureFlag, title: 'Inbox', titleKey: 'nav.inbox' }
  },
  {
    path: '/:workspaceId/inbox/:queue',
    redirect: to => {
      const queue = String(to.params.queue)
      const tab = ['all', 'drafts', 'waiting', 'done'].includes(queue) ? queue : null
      return {
        path: `/${to.params.workspaceId}/inbox`,
        query: tab ? { ...to.query, tab } : to.query,
      }
    },
  },

  // Lists moved into Settings › Communication (2026-07-21) — a list is opt-in
  // config, so its home is /settings/lists. The old member roster page is
  // replaced by the Contacts list filter, so a list deep-link lands on Contacts
  // pre-filtered to that list. Redirects keep old bookmarks working.
  {
    path: '/:workspaceId/lists',
    redirect: to => `/${to.params.workspaceId}/settings/lists`
  },
  {
    path: '/:workspaceId/lists/:listId',
    redirect: to => ({
      path: `/${to.params.workspaceId}/contacts`,
      // Everyone on the list, including people who have not answered yet.
      query: { list: String(to.params.listId), answered: 'all' },
    })
  },

  // Payouts (Admin)
  {
    path: '/:workspaceId/payouts',
    name: 'payouts',
    component: () => import('@/pages/app/PayoutsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Payouts', titleKey: 'nav.payouts' }
  },
  {
    path: '/:workspaceId/payouts/:payoutId',
    name: 'payout-detail',
    component: () => import('@/pages/app/PayoutDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Payout', titleKey: 'routeTitle.payout' }
  },

  // Reports — cross-channel performance overview (performanceEngine read side)
  {
    path: '/:workspaceId/reports',
    name: 'reports-overview',
    component: () => import('@/pages/app/reports/ReportsOverviewPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Reports', titleKey: 'routeTitle.reports' }
  },

  {
    path: '/:workspaceId/reports/email',
    name: 'reports-email',
    component: () => import('@/pages/app/content/EmailResultsPage.vue'),
    props: { aggregate: true },
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'email' as FeatureFlag, title: 'Email results', titleKey: 'routeTitle.emailResults' }
  },

  {
    path: '/:workspaceId/reports/social',
    name: 'reports-social',
    component: () => import('@/pages/app/reports/SocialResultsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'social' as FeatureFlag, title: 'Social results', titleKey: 'routeTitle.socialResults' }
  },

  // Forms — website submission totals and traffic sources.
  {
    path: '/:workspaceId/reports/forms',
    name: 'reports-forms',
    component: () => import('@/pages/app/reports/FormsReportPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'pages' as FeatureFlag, title: 'Forms', titleKey: 'formReport.title' }
  },

  // Journey — the funnel by stage (crm-lite-plan.md step 10). Contacts,
  // Leads and Deals share one feature switch: `customers`.
  {
    path: '/:workspaceId/reports/journey',
    name: 'reports-journey',
    component: () => import('@/pages/app/reports/JourneyReportPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'customers' as FeatureFlag, title: 'Journey', titleKey: 'routeTitle.journey' }
  },

  // Integrations
  {
    path: '/:workspaceId/integrations',
    name: 'integrations',
    component: () => import('@/pages/app/integrations/IntegrationsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'integrations' as FeatureFlag, title: 'Integrations', titleKey: 'nav.integrations' }
  },
  {
    path: '/:workspaceId/integrations/installed',
    name: 'integrations-installed',
    component: () => import('@/pages/app/integrations/InstalledIntegrationsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'integrations' as FeatureFlag, title: 'Installed Integrations', titleKey: 'routeTitle.installedIntegrations' }
  },
  {
    path: '/:workspaceId/integrations/connections/:connectionId',
    name: 'automation-connection',
    component: () => import('@/pages/app/integrations/ConnectionDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'integrations' as FeatureFlag, title: 'Connection', titleKey: 'routeTitle.connection' }
  },
  {
    // Agents — the merchant-facing automations surface. Its own top-level
    // section, sitting next to Integrations in the sidebar (not a tab of it):
    // the agent builder's list, and one page per agent below. (The older
    // template gallery, installed-workflows list and approvals inbox were
    // removed on 2026-10-19; the builder is the one way to make agents.)
    path: '/:workspaceId/agents',
    name: 'agents',
    component: () => import('@/pages/app/agents/AutomationAgentsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'integrations' as FeatureFlag, title: 'Agents', titleKey: 'nav.agents' }
  },

  {
    path: '/:workspaceId/agents/:agentId(\\d+)',
    name: 'automation-agent',
    component: () => import('@/pages/app/agents/AutomationAgentDetailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'integrations' as FeatureFlag, title: 'Agent', titleKey: 'routeTitle.agent' }
  },

  // Settings — single page, path-based tab routing.
  //   /:workspaceId/settings              → defaults to General tab
  //   /:workspaceId/settings/:tab         → loads the matching tab
  // Tab slugs come from config/settingsNav.ts; Communication includes Inbox
  // channel setup alongside lists, order confirmation, and events. (Sending
  // mailboxes and their warm-up live in Prospecting › Mailboxes.)
  // Legacy aliases (handled in TeamSettingsPage's onMounted alias map,
  // rewritten to canonical on landing): site-defaults → defaults,
  // scripts / custom-code → code.
  // TeamSettingsPage also accepts the legacy `?tab=` query and rewrites
  // to the path-based URL on mount for old bookmarks / external links.
  {
    path: '/:workspaceId/settings/:tab?',
    name: 'settings',
    component: () => import('@/pages/app/settings/TeamSettingsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Settings', titleKey: 'routeTitle.settings', navVariant: 'settings' }
  },
  // (Dedicated /:wsid/settings/{domains,custom-code,theme} routes removed
  // 2026-05-27 — these slugs now flow through the unified /settings/:tab?
  // route above. DomainsSettings / CustomCodeSettings / ThemeSettings page
  // components are still rendered inside TeamSettingsPage as the matching
  // tab. No code references the old `settings-{domains,custom-code,theme}`
  // route names — checked before removing.)

  // Profile — four sections in the left menu, each keeping its own address.
  // Booking links follow the same
  // feature switch as Contacts, Leads and Deals; the public booking pages are
  // bookingPublicRoutes below.
  {
    path: '/:workspaceId/profile',
    name: 'profile',
    component: () => import('@/pages/app/profile/ProfilePage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Profile', titleKey: 'routeTitle.profile', navVariant: 'profile' }
  },
  {
    path: '/:workspaceId/profile/email',
    name: 'profile-email',
    component: () => import('@/pages/app/profile/EmailPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Email', titleKey: 'routeTitle.profileEmail', navVariant: 'profile' }
  },
  {
    path: '/:workspaceId/profile/calendar',
    name: 'profile-calendar',
    component: () => import('@/pages/app/profile/CalendarPage.vue'),
    meta: { layout: 'app', requiresAuth: true, requiredFeature: 'customers' as FeatureFlag, title: 'Calendar', titleKey: 'routeTitle.profileCalendar', navVariant: 'profile' }
  },
  {
    path: '/:workspaceId/profile/notifications',
    name: 'profile-notifications',
    component: () => import('@/pages/app/profile/NotificationsPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Notifications', titleKey: 'routeTitle.notifications', navVariant: 'profile' }
  },

  // Members
  {
    path: '/:workspaceId/members',
    name: 'members',
    component: () => import('@/pages/app/MembersPage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Members', titleKey: 'routeTitle.members' }
  },

  // Subscription
  {
    path: '/:workspaceId/subscription',
    name: 'subscription',
    component: () => import('@/pages/app/SubscriptionPageUS.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Subscription', titleKey: 'routeTitle.subscription' }
  },

  // Usage — AI credits + storage + data transfer for the current billing period.
  {
    path: '/:workspaceId/usage',
    name: 'usage',
    component: () => import('@/pages/app/UsagePage.vue'),
    meta: { layout: 'app', requiresAuth: true, title: 'Usage', titleKey: 'routeTitle.usage' }
  },
  // Legacy /ai-usage bookmarks + links land on the renamed Usage page.
  {
    path: '/:workspaceId/ai-usage',
    redirect: to => `/${to.params.workspaceId}/usage`
  },

  // Create Workspace
  {
    path: '/:workspaceId/create',
    name: 'create-team-legacy',
    redirect: '/create'
  }
]

// Auth routes
const authRoutes: RouteRecordRaw[] = [
  {
    path: '/auth',
    name: 'auth-init',
    component: () => import('@/pages/auth/AuthInitPage.vue'),
    meta: { layout: 'none', title: 'Authentication', titleKey: 'routeTitle.authentication' }
  },
  {
    path: '/auth/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { layout: 'none', title: 'Login', titleKey: 'routeTitle.login' }
  },
  {
    path: '/auth/signup',
    name: 'register',
    component: () => import('@/pages/auth/SignupPage.vue'),
    meta: { layout: 'none', title: 'Sign Up', titleKey: 'routeTitle.signUp' }
  },
  {
    path: '/auth/logout',
    name: 'logout',
    component: () => import('@/pages/auth/LogoutPage.vue'),
    meta: { layout: 'none', title: 'Logout', titleKey: 'routeTitle.logout' }
  },
  {
    path: '/invitations/accept/:invitationToken/:hasAccount?',
    name: 'invitation',
    component: () => import('@/pages/auth/InvitationPage.vue'),
    meta: { layout: 'auth', title: 'Accept Invitation', titleKey: 'routeTitle.acceptInvitation' }
  }
]

// Public routes
const publicRoutes: RouteRecordRaw[] = [
  {
    path: '/welcome',
    name: 'welcome',
    component: () => import('@/pages/public/WelcomeUS.vue'),
    meta: { layout: 'public', requiresAuth: true, title: 'Welcome', titleKey: 'routeTitle.welcome' }
  },
  {
    path: '/create',
    name: 'create-team',
    component: () => import('@/pages/app/CreateTeamUS.vue'),
    meta: { layout: 'public', requiresAuth: true, title: 'Create Team', titleKey: 'routeTitle.createTeam' }
  },
  {
    path: '/404',
    name: 'not-found',
    component: () => import('@/pages/public/NotFoundPage.vue'),
    meta: { layout: 'public', title: 'Page Not Found', titleKey: 'routeTitle.pageNotFound' }
  }
]

// Public booking pages on the app address: a visitor books time with a member
// (/book/:publicKey) and manages that one booking through their private link
// (/book/manage/:token). No sign-in, no app navigation, no workspace. Listed
// first so no /:workspaceId/... pattern can claim /book/...
const bookingPublicRoutes: RouteRecordRaw[] = [
  {
    path: '/book/manage/:token',
    name: 'booking-manage',
    component: () => import('@/pages/public/bookings/ManageBookingPage.vue'),
    meta: { layout: 'public', title: 'Your booking', titleKey: 'routeTitle.yourBooking' }
  },
  {
    path: '/book/:workspaceSlug/:slug',
    name: 'booking-address',
    component: () => import('@/pages/public/bookings/PublicBookingPage.vue'),
    meta: { layout: 'public', title: 'Book a time', titleKey: 'routeTitle.bookATime' }
  },
  {
    path: '/book/:publicKey',
    name: 'booking-public',
    component: () => import('@/pages/public/bookings/PublicBookingPage.vue'),
    meta: { layout: 'public', title: 'Book a time', titleKey: 'routeTitle.bookATime' }
  }
]

// Root redirect and catch-all
const rootRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'landing',
    component: () => import('@/pages/public/LandingPage.vue'),
    meta: { layout: 'public', title: 'Welcome', titleKey: 'routeTitle.welcome' }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

const routes: RouteRecordRaw[] = [
  ...bookingPublicRoutes,
  ...appRoutes,
  ...authRoutes,
  ...publicRoutes,
  ...rootRoutes
]

/** The route's title in the user's language (English until that language's words have loaded). */
export function routeTitle(meta: RouteMeta): string | undefined {
  const key = meta.titleKey
  if (key && i18n.global.te(key)) return i18n.global.t(key)
  return meta.title
}

function applyDocumentTitle(meta: RouteMeta) {
  const title = routeTitle(meta)
  document.title = title ? `${title} - ProfitJam` : 'ProfitJam'
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

// Retire the old stage query before opening the same editor route record.
router.beforeEach(to => canonicalContentWorkflowLocation(to))

// Navigation guard for auth + workspace check
router.beforeEach(async (to, from, next) => {
  // Update document title
  applyDocumentTitle(to.meta)

  // Settings "back" memory — when the user enters settings from another page
  // in the SAME workspace, remember where they came from so the sidebar's
  // back row can return them there. Deep links and cross-workspace entries
  // clear it (back falls to Home); tab moves inside settings keep it.
  if (to.meta.navVariant === 'settings' && from.meta.navVariant !== 'settings') {
    const sameWorkspace =
      !!to.params.workspaceId && from.params.workspaceId === to.params.workspaceId
    useUIStore().setSettingsReturnTo(sameWorkspace ? from.fullPath : null)
  }

  // Keep Profile's return target across section changes, but never across workspaces.
  if (to.meta.navVariant === 'profile' && (
    from.meta.navVariant !== 'profile' || from.params.workspaceId !== to.params.workspaceId
  )) {
    const sameWorkspace =
      !!to.params.workspaceId && from.params.workspaceId === to.params.workspaceId
    useUIStore().setProfileReturnTo(sameWorkspace ? from.fullPath : null)
  }

  // Knowledge "back" memory — the parallel of the settings rule above, kept
  // separate so entering one section-takeover mode never rewrites the other's
  // back target.
  if (to.meta.navVariant === 'knowledge' && from.meta.navVariant !== 'knowledge') {
    const sameWorkspace =
      !!to.params.workspaceId && from.params.workspaceId === to.params.workspaceId
    useUIStore().setKnowledgeReturnTo(sameWorkspace ? from.fullPath : null)
  }

  // Only guard routes that require auth
  if (!to.meta.requiresAuth) {
    next()
    return
  }

  const authStore = useAuthStore()

  if (!authStore.isAuthChecked) await authStore.checkAuth()

  // If not authenticated, redirect to login
  if (!authStore.isAuthenticated) {
    if (authStore.errorCode && authStore.errorCode !== 'AUTHENTICATION_REQUIRED') {
      next({ name: 'auth-init', query: { error: authErrorWord(authStore.errorCode), redirect: to.fullPath } })
      return
    }
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  if (to.name === 'welcome') {
    next()
    return
  }

  // Ensure workspaces are loaded
  const workspaceStore = useWorkspaceStore()
  if (workspaceStore.workspaces.length === 0) {
    try {
      await workspaceStore.fetchWorkspaces()
    } catch {
      // fetch failed — send to welcome, they can retry
    }
  }

  // If user has no workspaces, force them to the welcome flow
  if (workspaceStore.workspaces.length === 0) {
    next({ name: 'welcome' })
    return
  }

  // Verify the user owns the requested workspace
  const workspaceId = to.params.workspaceId as string | undefined
  if (workspaceId) {
    let targetWorkspace = workspaceStore.workspaces.find(w => String(w.id) === workspaceId)

    // Internal staff (INTERNAL_ADMIN_MEMBER_IDS) can open workspaces they are
    // not a member of — those never appear in the membership list, so a plain
    // `.find` misses them and we'd bounce to the default workspace. Try loading
    // the workspace directly: GET /workspaces/:id applies the backend
    // internal-admin bypass and 403/404s when there is genuinely no access.
    // This is what makes the admin "Log in to workspace" deep-link land.
    if (!targetWorkspace) {
      try {
        await workspaceStore.fetchWorkspace(workspaceId)
        targetWorkspace = workspaceStore.currentWorkspace ?? undefined
      } catch {
        // no access — fall through to the default-workspace bounce below
      }
    }

    if (!targetWorkspace) {
      next({ name: 'dashboard', params: { workspaceId: workspaceStore.workspaces[0]!.id } })
      return
    }

    // Ensure currentWorkspace matches the route's workspace so hasFeature reads the right entitlements
    if (String(workspaceStore.currentWorkspace?.id) !== workspaceId) {
      workspaceStore.setCurrentWorkspace(targetWorkspace)
    }

    // Check feature flag on route. Only TRULY-hidden features (i.e. the
    // feature doesn't exist for this workspace's mode at all) redirect to
    // subscription. `gated` features let the user navigate to the page —
    // the page itself shows a preview with an inline upgrade prompt. This
    // gives free/basic users a chance to SEE what they're missing rather
    // than getting silently shuffled away.
    const requiredFeature = to.name === 'settings' && to.params.tab === 'website-chat'
      ? 'inbox' : to.meta.requiredFeature as FeatureFlag | undefined
    const access = requiredFeature ? workspaceStore.getCapabilityAccess(requiredFeature) : 'full'
    const restrictedCapability = requiredFeature && ['campaigns', 'email.sequences', 'inbox', 'prospecting'].includes(requiredFeature)
    if (access === 'hidden' || (restrictedCapability && access === 'gated')) {
      next({ name: 'subscription', params: { workspaceId } })
      return
    }
  }

  next()
})

// Track PageView on every SPA route change (Meta Pixel + Conversions API)
// Skip auth routes — they're transient redirects, not marketing-meaningful views.
// Skip the public booking pages too: their visitors are a member's guests, not
// ProfitJam's, and a guest's private link must never reach an ad platform.
router.afterEach((to) => {
  if (to.path.startsWith('/auth') || to.path.startsWith('/book/')) return
  import('@/utils/metaPixel').then(({ trackEvent }) => {
    trackEvent('PageView')
  })
  // Dogfood: feed our OWN Custom Events with a page visit (contact = the
  // logged-in user). No-op until a browser key is configured / before login.
  import('@/services/selfEvents').then(({ trackSelfEvent }) => {
    trackSelfEvent('page_view', { path: to.path, name: String(to.name || '') })
  })
})

// Chunk-load-failure recovery — when the deployed build replaces the
// hashed asset chunks (frequent on staging), an old shell's lazy-loaded
// route components 404 to the SPA fallback (index.html), which the
// browser refuses to execute as a JS module. Symptom: navigation hangs,
// console shows "Failed to fetch dynamically imported module" or
// "Expected a JavaScript-or-Wasm module script but the server responded
// with a MIME type of 'text/html'". Recovery is to force-reload so the
// user picks up the new shell + matching chunks.
//
// Guarded by sessionStorage so we don't reload-loop if a chunk is
// genuinely broken (e.g., CDN propagation half-state) — a second
// failure inside the same session falls through to the normal error
// surface instead of looping.
const CHUNK_RELOAD_FLAG = 'pj_chunk_reload_attempted'
function isChunkLoadFailure(err: unknown): boolean {
  if (!err) return false
  const msg = (err instanceof Error ? err.message : String(err)) || ''
  return (
    msg.includes('Failed to fetch dynamically imported module')
    || msg.includes('error loading dynamically imported module')
    || msg.includes('Importing a module script failed')
    || /Loading (chunk|CSS chunk) \d+ failed/.test(msg)
    || (msg.includes('MIME type') && msg.includes('text/html'))
  )
}
router.onError((err) => {
  if (!isChunkLoadFailure(err)) return
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1')
    // eslint-disable-next-line no-console
    console.warn('[router] chunk load failed — reloading to pick up new shell')
    window.location.reload()
  } catch {
    // sessionStorage unavailable (private mode quotas, etc.) — still
    // attempt the reload; a one-time loop is preferable to a stuck app.
    window.location.reload()
  }
})
// Clear the chunk-reload guard after any successful navigation so a
// second failure later in the session (e.g., another deploy lands while
// the user keeps working) still gets one auto-reload chance.
router.afterEach(() => {
  try { sessionStorage.removeItem(CHUNK_RELOAD_FLAG) } catch { /* noop */ }
})

// Re-title the browser tab when the language switches (its words load after the first navigation).
watch(i18n.global.locale, () => applyDocumentTitle(router.currentRoute.value.meta))

export default router
